import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import PhotoCheck, { usePhotoNeeded } from "@/components/candidate/PhotoCheck";
import { useTenantBrand, BrandMark, BrandBar, PoweredBy } from "@/components/candidate/CandidateBrand";
import { VideoFrame, ControlBar, TranscriptPanel, skillTerms } from "@/components/candidate/VideoStage";
import { AvaTile, AVA_INTRO_TEXT } from "@/components/candidate/AvaTile";
import { useMicLevel, MicMeter } from "@/components/candidate/MicMeter";
import { Mic, MicOff, ArrowRight, AlertTriangle, Loader2, Volume2, Upload } from "lucide-react";

/**
 * Real AI Video Interview with:
 *  • Realistic OpenAI TTS voice (fallback: browser speechSynthesis)
 *  • Continuous webcam + mic MediaRecorder
 *  • Live SpeechRecognition transcript
 *  • AI always finishes speaking; the mic answer window opens afterwards
 *  • Dynamic follow-ups via /api/video/next
 *  • Full recording upload to B2 on finalize
 */
export default function VideoRoom() {
  const { token } = useParams();
  const brand = useTenantBrand(token);
  const [jobSkills, setJobSkills] = useState([]);
  useEffect(() => {
    api.get(`/invite/${token}`).then(r => setJobSkills([...(r.data?.job?.must_haves || []), ...(r.data?.job?.nice_to_haves || [])])).catch(() => {});
  }, [token]);
  const [photoNeeded, photoDone] = usePhotoNeeded(token, "video");
  const nav = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [resumeInfo, setResumeInfo] = useState(null);   // {question_index, next_question} when continuing an interrupted interview
  const [startError, setStartError] = useState("");
  const attemptRef = useRef(0);
  const partTimerRef = useRef(null);
  const partStateRef = useRef({ sent: 0, seq: 0, busy: false, bytes: 0 });
  const [idx, setIdx] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [listening, setListening] = useState(false);
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [introPlaying, setIntroPlaying] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const ttsCacheRef = useRef(new Map());
  const fetchTts = (text) => {
    const ctl = new AbortController();
    const t = setTimeout(() => ctl.abort(), 20000);
    return fetch(`${API}/tts`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text, voice: "nova" }), signal: ctl.signal })
      .then((r) => (r.ok ? r.arrayBuffer() : null)).catch(() => null).finally(() => clearTimeout(t));
  };
  // Start generating Ava's voice early (e.g. while the intro clip plays) so it starts without a gap.
  const prefetchTts = (text) => { if (!ttsCacheRef.current.has(text)) ttsCacheRef.current.set(text, fetchTts(text)); };
  const [proctorFlags, setProctorFlags] = useState({ tab_switches: 0 });
  const [started, setStarted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ pct: 0, loaded: 0, total: 0 });
  const [uploadError, setUploadError] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState("");

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const speechRef = useRef(null);
  const audioElRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const monitorRafRef = useRef(null);
  const answerStartRef = useRef(0);
  const transcriptRef = useRef("");
  const answeringRef = useRef(false);   // true while we're waiting for the candidate's answer
  const baseTextRef = useRef("");       // text kept across speech-recognition restarts
  const [submitting, setSubmitting] = useState(false);
  const [preparing, setPreparing] = useState(false);   // answer accepted, next question's voice loading
  const [isFollowUp, setIsFollowUp] = useState(false);
  useEffect(() => { if (aiSpeaking) setPreparing(false); }, [aiSpeaking]);
  const lastResultAtRef = useRef(0);
  const srStartedAtRef = useRef(0);
  const [typeMode, setTypeMode] = useState(false);   // typed-answer fallback when speech capture fails
  const emptyRestartsRef = useRef(0);
  const hiddenPauseRef = useRef(false);
  const hiddenAtRef = useRef(0);
  const resumeMicRef = useRef(null);
  const typeModeRef = useRef(false);
  useEffect(() => { typeModeRef.current = typeMode; }, [typeMode]);
  const editingRef = useRef(false);
  const switchTypeMode = (on) => {
    if (!on) editingRef.current = false;
    setTypeMode(on); typeModeRef.current = on;
    if (on) { const r = speechRef.current; speechRef.current = null; try { r?.stop(); } catch (_) {} setListening(false); }
    else if (answeringRef.current && !submitting) { emptyRestartsRef.current = 0; startListening(false); }
  };
  const hasSR = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  const interruptAbortRef = useRef(null);
  // --- Audio mixer: merges mic + AI TTS into a single stream so the
  // --- MediaRecorder captures BOTH sides of the conversation.
  const mixerDestRef = useRef(null);
  const ttsSourceRef = useRef(null);
  // Timeline capture for post-interview AI-audio merge. recordStartMsRef
  // is set when MediaRecorder.start() fires (same monotonic clock as the
  // webm's internal timestamps). aiClipsRef holds each played TTS clip
  // with its exact offset_ms (when it started) and end_ms (when it ended
  // or was interrupted). On finalize these go to the server which uses
  // ffmpeg to overlay them into the recording at the exact millisecond.
  const recordStartMsRef = useRef(0);
  const aiClipsRef = useRef([]);
  const activeClipRef = useRef(null);
  const sessionIdRef = useRef(null);
  const idxRef = useRef(0);
  const spokenInterruptedRef = useRef(false);

  useEffect(() => { transcriptRef.current = transcript; }, [transcript]);
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
  useEffect(() => { idxRef.current = idx; }, [idx]);

  // Fire-and-forget conversation turn logger — keeps a text transcript of
  // the full AI↔candidate exchange on the server, paired with the audio
  // recording. Never blocks UX; swallow network errors.
  const logTurn = (role, text, extra = {}) => {
    const sid = sessionIdRef.current;
    if (!sid || !text) return;
    api.post(`/video/${sid}/log-turn`, { role, text, question_index: idxRef.current, ...extra }).catch(() => {});
  };

  useEffect(() => {
    let m = true;
    (async () => {
      try {
        const { data } = await api.post(`/video/${token}/start`);
        if (!m) return;
        setSessionId(data.session_id);
        setQuestions(data.questions);
        attemptRef.current = data.attempt || 0;
        if (data.resume) setResumeInfo(data.resume);
      } catch (e) { setStartError(fmtErr(e.response?.data?.detail) || "Couldn't start the interview."); }
    })();
    const onVis = () => {
      if (document.hidden) { hiddenAtRef.current = Date.now(); setProctorFlags(p => ({ ...p, tab_switches: p.tab_switches + 1 })); return; }
      if (hiddenAtRef.current && sessionIdRef.current) {
        const away = Date.now() - hiddenAtRef.current; hiddenAtRef.current = 0;
        api.post(`/video/${sessionIdRef.current}/proctor`, { away_ms: away }).catch(() => {});
      }
      // Back on the tab: Chrome stops speech recognition in background tabs — restart it.
      if (hiddenPauseRef.current && answeringRef.current && !typeModeRef.current) {
        hiddenPauseRef.current = false;
        resumeMicRef.current?.();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      m = false;
      answeringRef.current = false;
      document.removeEventListener("visibilitychange", onVis);
      try { speechRef.current?.stop(); } catch(_){}
      try { recRef.current?.stop(); } catch(_){}
      clearInterval(partTimerRef.current);
      try { window.speechSynthesis.cancel(); } catch(_){}
      try { audioElRef.current?.pause(); } catch(_){}
      cancelAnimationFrame(monitorRafRef.current);
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, [token]);

  const beginInterview = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = s;

      // Build an AudioContext ONLY for TTS playback + interrupt detection.
      // Earlier we also piped the mic through this context and routed a
      // MediaStreamDestination into MediaRecorder so AI audio would land in
      // the recording — but that pattern (webrtc video track + AudioContext
      // audio track in one MediaStream) is a Chrome weak spot: when the AC
      // suspends between TTS clips the audio track starves, MediaRecorder
      // emits malformed chunks, and the blob either hangs during upload or
      // never finalizes. Coding uploads work fine because they record the
      // raw getUserMedia stream directly, so we now do the same here.
      // The AI side of the conversation is still fully captured as text in
      // `conversation[]` on the session doc and shown to the recruiter in
      // the Video Interview Transcript panel.
      const AC = window.AudioContext || window.webkitAudioContext;
      audioCtxRef.current = new AC();

      // Interrupt analyser on the mic.
      try {
        const micSrc = audioCtxRef.current.createMediaStreamSource(s);
        const an = audioCtxRef.current.createAnalyser();
        an.fftSize = 1024; an.smoothingTimeConstant = 0.8;
        micSrc.connect(an);
        analyserRef.current = an;
      } catch(_){}

      // Record the raw getUserMedia stream directly — same pattern as
      // CodingRoom.jsx. Bitrate capped so a 10-min interview is ~30 MB.
      const RECORDER_OPTS = { videoBitsPerSecond: 400_000, audioBitsPerSecond: 48_000 };
      try {
        const mr = new MediaRecorder(s, { mimeType: "video/webm;codecs=vp9,opus", ...RECORDER_OPTS });
        mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
        mr.start(1000);
        recordStartMsRef.current = performance.now();
        recRef.current = mr;
      } catch (e) {
        // Codec unsupported — let Chrome pick but still cap bitrate.
        const mr = new MediaRecorder(s, RECORDER_OPTS);
        mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
        mr.start(1000);
        recordStartMsRef.current = performance.now();
        recRef.current = mr;
      }

      setStarted(true);
      if (!resumeInfo) setIntroPlaying(true);
      // The <video> element only mounts after `started` flips to true, so
      // attach the stream on the next tick (otherwise videoRef.current is null).
      setTimeout(async () => {
        if (videoRef.current && streamRef.current) {
          videoRef.current.srcObject = streamRef.current;
          videoRef.current.play?.().catch(() => {});
        }
        // Safety net: upload the recording in ~20s parts so a closed browser keeps what was recorded.
        partTimerRef.current = setInterval(uploadPart, 20000);
        if (resumeInfo) {
          setIdx(resumeInfo.question_index);
          askQuestion(resumeInfo.question_index, resumeInfo.next_question || questions[resumeInfo.question_index] || "", "Welcome back — let's pick up where we left off. ");
        } else {
          const intro = speak(AVA_INTRO_TEXT);
          setTimeout(() => prefetchTts("Let's get started. " + questions[0]), 250);
          await intro;
          setIntroPlaying(false);
          askQuestion(0, questions[0], "Let's get started. ");
        }
      }, 400);
    } catch (e) {
      toast.error("We need camera + mic to run the interview. Please allow access and reload.");
    }
  };

  const stopWatchForInterrupt = () => { cancelAnimationFrame(monitorRafRef.current); };

  // Speak `text` with the AI voice AND capture the exact mp3 bytes + the
  // precise millisecond offset into the recording timeline. These are
  // uploaded alongside the main webm so ffmpeg can overlay the AI audio
  // onto the recording at the exact moment it played live — producing a
  // single audit-grade file that sounds like a real one-to-one conversation.
  const speak = async (text) => new Promise(async (resolve) => {
    setAiSpeaking(true);
    spokenInterruptedRef.current = false;
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      setAiSpeaking(false);
      setVoiceOn(false);
      stopWatchForInterrupt();
      // Finalize the clip's end_ms if we have an active one. For interrupts
      // this gets stamped at the exact interrupt moment so ffmpeg trims
      // the AI clip there (candidate's voice takes over cleanly).
      if (activeClipRef.current && activeClipRef.current.end_ms == null) {
        activeClipRef.current.end_ms = performance.now() - recordStartMsRef.current;
        activeClipRef.current.interrupted = !!spokenInterruptedRef.current;
      }
      activeClipRef.current = null;
      // Persist this AI turn text to the server so the recruiter still
      // sees it in the transcript panel even if ffmpeg merge fails.
      logTurn("ai", text, { interrupted: spokenInterruptedRef.current });
      resolve();
    };
    interruptAbortRef.current = done;
    try {
      const pending = ttsCacheRef.current.get(text) || fetchTts(text);
      ttsCacheRef.current.delete(text);
      const arrayBuf = await pending;
      if (arrayBuf && audioCtxRef.current) {
        // Same mp3 bytes are used for playback and for the recording upload.
        const mp3Blob = new Blob([arrayBuf.slice(0)], { type: "audio/mpeg" });
        const audioBuf = await audioCtxRef.current.decodeAudioData(arrayBuf.slice(0));
        const src = audioCtxRef.current.createBufferSource();
        src.buffer = audioBuf;
        src.connect(audioCtxRef.current.destination); // speakers only
        ttsSourceRef.current = src;
        // Timeline capture: record the exact moment this clip starts.
        const clipEntry = {
          index: aiClipsRef.current.length,
          offset_ms: performance.now() - recordStartMsRef.current,
          duration_ms: Math.round(audioBuf.duration * 1000),
          end_ms: null,
          interrupted: false,
          blob: mp3Blob,
          text,
        };
        aiClipsRef.current.push(clipEntry);
        activeClipRef.current = clipEntry;
        src.onended = () => {
          ttsSourceRef.current = null;
          if (activeClipRef.current === clipEntry && clipEntry.end_ms == null) {
            clipEntry.end_ms = performance.now() - recordStartMsRef.current;
          }
          done();
        };
        try { await audioCtxRef.current.resume?.(); } catch (_) {}
        try { src.start(0); setVoiceOn(true); } catch { done(); return; }
        // Safety net: if the audio context stalls, never leave the candidate waiting.
        setTimeout(done, Math.round(audioBuf.duration * 1000) + 5000);
        return;
      }
      // Fallback: browser speechSynthesis — plays through the OS and will
      // NOT be captured by the recorder. We still log the text so the
      // recruiter sees the question even though the audio is missing.
      const u = new SpeechSynthesisUtterance(text);
      const voices = window.speechSynthesis.getVoices();
      const prefer = voices.find(v => /natural|neural|samantha|google us english|ava|aria/i.test(v.name) && v.lang?.startsWith("en"))
        || voices.find(v => v.lang?.startsWith("en"));
      if (prefer) u.voice = prefer;
      u.onstart = () => setVoiceOn(true);
      u.onend = done; u.onerror = done;
      window.speechSynthesis.speak(u);
      // Chrome sometimes never fires onend for speechSynthesis.
      setTimeout(done, Math.max(6000, text.split(/\s+/).length * 450 + 4000));
    } catch (e) { done(); }
  });

  const askQuestion = async (qIdx, qText, spokenPrefix = "") => {
    setTranscript(""); transcriptRef.current = ""; baseTextRef.current = "";
    // "Edit" is a one-answer switch; go back to voice for the next question.
    if (editingRef.current) { editingRef.current = false; setTypeMode(false); typeModeRef.current = false; }
    setCurrentQuestion(qText);
    await speak(spokenPrefix + qText);
    setPreparing(false);
    startListening();
  };

  // Chrome's speech recognition stops by itself after silence, ~60s of talking or a
  // network blip. While an answer is in progress we restart it and keep the text.
  const startListening = (reset = true) => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (reset) { emptyRestartsRef.current = 0; }
    if (!SR || typeModeRef.current) {
      if (reset) { setTranscript(""); transcriptRef.current = ""; answerStartRef.current = Date.now(); }
      answeringRef.current = true; setTypeMode(true); return;
    }
    if (reset) {
      setTranscript(""); transcriptRef.current = ""; baseTextRef.current = ""; answerStartRef.current = Date.now();
    } else {
      baseTextRef.current = (transcriptRef.current || "").trim() ? transcriptRef.current.trim() + " " : "";
    }
    answeringRef.current = true;
    const r = new SR(); r.continuous = true; r.interimResults = true; r.lang = "en-US";
    let finalText = "";
    srStartedAtRef.current = Date.now(); lastResultAtRef.current = Date.now();
    r.onresult = (e) => {
      // Late results from a stopped recognizer must not repaint an old answer.
      if (speechRef.current !== r || !answeringRef.current) return;
      lastResultAtRef.current = Date.now();
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += t + " "; else interim += t;
      }
      emptyRestartsRef.current = 0;
      const text = baseTextRef.current + finalText + interim;
      transcriptRef.current = text;
      setTranscript(text);
    };
    r.onerror = (e) => {
      if (document.hidden) return;
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        speechRef.current = null; setListening(false); setTypeMode(true); typeModeRef.current = true;
        toast.error("Voice capture is blocked in this browser — please type your answer instead.");
      }
    };
    const startedAt = Date.now();
    r.onend = () => {
      if (speechRef.current !== r) return;
      if (!answeringRef.current) { setListening(false); return; }
      // Tab in the background: don't fight Chrome — resume when the candidate comes back.
      if (document.hidden) { hiddenPauseRef.current = true; setListening(false); return; }
      // Only instant failures count (silence while thinking is normal and ends after ~8s).
      if (Date.now() - startedAt > 1500) emptyRestartsRef.current = 0;
      else emptyRestartsRef.current += 1;
      if (emptyRestartsRef.current > 6) {
        speechRef.current = null; setListening(false); setTypeMode(true); typeModeRef.current = true;
        toast.error("We can't pick up your voice — you can type your answer below, or click Resume mic.");
        return;
      }
      setTimeout(() => { if (answeringRef.current && speechRef.current === r) startListening(false); }, 250);
    };
    speechRef.current = r;
    try { r.start(); setListening(true); } catch(_){ setListening(false); }
  };
  resumeMicRef.current = () => { emptyRestartsRef.current = 0; startListening(false); };

  const editTranscript = () => { editingRef.current = true; switchTypeMode(true); };
  const clearTranscript = () => {
    const r = speechRef.current; speechRef.current = null;
    try { r?.abort ? r.abort() : r?.stop(); } catch (_) {}
    setTranscript(""); transcriptRef.current = ""; baseTextRef.current = "";
    if (typeModeRef.current) return;
    setListening(false);
    if (answeringRef.current) setTimeout(() => { if (answeringRef.current && !speechRef.current) startListening(false); }, 300);
  };

  const [micLevel, micLevelRef] = useMicLevel(streamRef.current, started);
  // Watchdog: Chrome sometimes keeps "listening" after a pause but stops sending words.
  // If the mic hears speech for a while with no new words, restart recognition.
  useEffect(() => {
    if (!started) return;
    let loudMs = 0;
    const t = setInterval(() => {
      if (!answeringRef.current || typeModeRef.current || document.hidden || !speechRef.current) { loudMs = 0; return; }
      loudMs = micLevelRef.current > 0.08 ? loudMs + 500 : Math.max(0, loudMs - 250);
      const now = Date.now();
      if (loudMs >= 1500 && now - lastResultAtRef.current > 3000 && now - srStartedAtRef.current > 3000) {
        loudMs = 0;
        const r = speechRef.current; speechRef.current = null;
        try { r.abort ? r.abort() : r.stop(); } catch (_) {}
        resumeMicRef.current?.();
      }
    }, 500);
    return () => clearInterval(t);
  }, [started]); // eslint-disable-line react-hooks/exhaustive-deps

  const postNext = async (body) => {
    for (let attempt = 0; ; attempt++) {
      try { return (await api.post("/video/next", body, { timeout: 45000 })).data; }
      catch (e) {
        const st = e.response?.status;
        if (attempt >= 2 || (st && st < 500 && st !== 429)) throw e;
        await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
      }
    }
  };

  const submitAnswer = async () => {
    if (submitting) return;
    const answer = (transcriptRef.current || transcript).trim();
    if (!answer) { toast.error("We didn't catch an answer — try again."); if (!listening) startListening(false); return; }
    answeringRef.current = false;
    const r = speechRef.current; speechRef.current = null;
    try { r?.stop(); } catch(_){}
    setListening(false);
    setSubmitting(true);
    try {
      const data = await postNext({ session_id: sessionId, question_index: idx, answer_text: answer });
      // Log the candidate's turn only once it's accepted, so retries don't duplicate it.
      logTurn("candidate", answer);
      setTranscript(""); transcriptRef.current = "";
      if (data.done) {
        setSubmitting(false);
        return finalize();
      }
      setPreparing(true);
      setSubmitting(false);
      const nextIdx = data.advance_to != null ? data.advance_to : idx;
      setIdx(nextIdx);
      setIsFollowUp(!!data.follow_up);
      // small human-like beat
      setTimeout(() => askQuestion(nextIdx, data.next_question), 350);
    } catch (e) {
      setSubmitting(false);
      toast.error("We couldn't send your answer — your text is kept. Please click Done answering again.");
      startListening(false);
    }
  };

  // Flush recorder chunks and POST the blob to the server. We use
  // XMLHttpRequest (not fetch) so we can expose live upload progress to
  // the candidate — a silent "Saving your interview recording…" screen with
  // no feedback is indistinguishable from a hung upload, and large webm
  // blobs over a slow connection can easily take a minute+.
  //
  // A 5-minute hard timeout prevents infinite hangs when the ingress or
  // backend drops the connection mid-stream. On error we DO NOT discard the
  // blob — it stays in `chunksRef.current` so a Retry button can re-upload
  // without forcing the candidate to re-record.
  const uploadRecording = async () => {
    if (recRef.current && recRef.current.state !== "inactive") {
      await new Promise((res) => {
        const mr = recRef.current;
        mr.onstop = () => res();
        try { mr.stop(); } catch { res(); }
      });
    }
    const blob = new Blob(chunksRef.current, { type: "video/webm" });
    if (blob.size === 0) return { ok: true, bytes: 0 };
    // Preferred path: most of the video is already in storage as 20s parts — send only the tail
    // and let the server join them. Falls back to the full upload below if anything goes wrong.
    try {
      const st = partStateRef.current;
      for (let i = 0; i < 60 && st.busy; i++) await new Promise(r => setTimeout(r, 500));
      for (let tries = 0; tries < 4 && chunksRef.current.length > st.sent; tries++) {
        await uploadPart();
        setUploadProgress({ pct: Math.round((st.bytes / blob.size) * 100), loaded: st.bytes, total: blob.size });
        if (chunksRef.current.length > st.sent) await new Promise(r => setTimeout(r, 1500 * (tries + 1)));
      }
      if (chunksRef.current.length <= st.sent && st.seq > 0) {
        await api.post(`/recordings/finalize/video/${sessionIdRef.current}?attempt=${attemptRef.current}&parts=${st.seq}`, null, { timeout: 120000 });
        setUploadProgress({ pct: 100, loaded: blob.size, total: blob.size });
        return { ok: true, bytes: blob.size };
      }
    } catch (_) { /* fall back to the single upload */ }
    const fd = new FormData();
    fd.append("file", blob, `video-${sessionId}.webm`);
    const url = `${API}/recordings/upload/video/${sessionId}`;
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url, true);
      // 5-minute hard cap. Even a 50 MB upload on 1 Mbps completes in ~7 min
      // worst-case; anything longer indicates a stall worth surfacing.
      xhr.timeout = 5 * 60 * 1000;
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) {
          setUploadProgress({
            pct: Math.round((ev.loaded / ev.total) * 100),
            loaded: ev.loaded,
            total: ev.total,
          });
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ ok: true, bytes: blob.size });
        } else {
          resolve({ ok: false, error: `upload ${xhr.status}: ${(xhr.responseText || "").slice(0, 200)}` });
        }
      };
      xhr.onerror = () => resolve({ ok: false, error: "network error — the upload connection was interrupted" });
      xhr.ontimeout = () => resolve({ ok: false, error: "upload timed out after 5 minutes" });
      try { xhr.send(fd); }
      catch (e) { resolve({ ok: false, error: e?.message || String(e) }); }
    });
  };

  const uploadPart = async () => {
    const st = partStateRef.current;
    const sid = sessionIdRef.current;
    if (st.busy || !sid || chunksRef.current.length <= st.sent) return;
    st.busy = true;
    const end = chunksRef.current.length;
    const fd = new FormData();
    const partBlob = new Blob(chunksRef.current.slice(st.sent, end), { type: "video/webm" });
    fd.append("file", partBlob, `part-${st.seq}.webm`);
    try {
      await api.post(`/recordings/part/video/${sid}?attempt=${attemptRef.current}&seq=${st.seq}`, fd, { timeout: 60000 });
      st.sent = end; st.seq += 1; st.bytes += partBlob.size;
    } catch (_) { /* retried on the next tick */ }
    st.busy = false;
  };

  const finalize = async () => {
    clearInterval(partTimerRef.current);
    // Check job's enabled stages so we don't send the user to a disabled coding round.
    let goCoding = true;
    try {
      const { data } = await api.get(`/invite/${token}`);
      goCoding = !!(data?.job?.stages?.coding);
    } catch (_) { /* keep default */ }
    try {
      const outro = goCoding
        ? "That's all my questions — thanks so much for the thoughtful answers. Give me a moment to save your recording, and we'll move on to the coding stage."
        : "That's all my questions — thanks so much for the thoughtful answers. Give me a moment to save your recording and your interview will be complete.";
      await speak(outro);
    } catch(_){}
    // Flip the UI into a "finalizing" state so the user sees progress while
    // we actually block on the upload. This replaces the old fire-and-forget
    // path that was dropping the video because the page was navigating away
    // before fetch could flush more than ~64 KB.
    setUploading(true);
    setUploadError(null);
    setUploadProgress({ pct: 0, loaded: 0, total: 0 });
    const result = await uploadRecording();
    if (!result.ok) {
      // Keep the user on this screen with a Retry button — the blob is still
      // in chunksRef.current so re-upload doesn't need a re-record.
      setUploadError(result.error || "Upload failed");
      // Fire-and-forget the session-complete so the AI side still sees the
      // interview as "done" — the recording retry doesn't block that.
      api.post(`/video/${sessionId}/complete`).catch(() => {});
      return;
    }
    // Main video is up. Now ship the AI audio timeline so the server can
    // ffmpeg-overlay the AI voice at the exact millisecond it played, giving
    // the recruiter a single file that sounds like a real conversation. We
    // fire-and-forget this: if it fails, the recruiter still has the mic+cam
    // recording to review; the merge is a nice-to-have on top.
    uploadAiTimeline().catch(() => {});
    try {
      streamRef.current?.getTracks().forEach(t => t.stop());
    } catch (_) {}
    api.post(`/video/${sessionId}/complete`).catch(() => {});
    if (result.bytes > 0) toast.success("Interview recording saved");
    else toast.success("Interview complete");
    setUploading(false);
    nav(goCoding ? `/invite/${token}/coding` : `/invite/${token}/done`);
  };

  // Send every captured AI mp3 clip + its millisecond-precise timeline to
  // the server. The server queues an ffmpeg job that overlays each clip onto
  // the main video at `offset_ms`, trimmed to `end_ms` for interrupts, and
  // stores the merged file as the primary playback URL on the session doc.
  const uploadAiTimeline = async () => {
    const clips = aiClipsRef.current.filter((c) => c.blob && c.end_ms != null);
    if (clips.length === 0) return;
    const fd = new FormData();
    const manifest = clips.map((c) => ({
      index: c.index,
      offset_ms: Math.max(0, Math.round(c.offset_ms)),
      duration_ms: c.duration_ms,
      end_ms: Math.round(c.end_ms),
      interrupted: !!c.interrupted,
    }));
    fd.append("manifest", JSON.stringify(manifest));
    clips.forEach((c) => fd.append(`clip_${c.index}`, c.blob, `clip_${c.index}.mp3`));
    await fetch(`${API}/video/${sessionId}/ai-timeline`, { method: "POST", body: fd });
  };

  // Retry upload without re-recording — reuses the blob still sitting in
  // chunksRef.current. Shown when the first upload fails (timeout, 413,
  // network drop, etc.) so the candidate doesn't lose their recording.
  const retryUpload = async () => {
    let goCoding = true;
    try {
      const { data } = await api.get(`/invite/${token}`);
      goCoding = !!(data?.job?.stages?.coding);
    } catch (_) {}
    setUploadError(null);
    setUploadProgress({ pct: 0, loaded: 0, total: 0 });
    const result = await uploadRecording();
    if (!result.ok) {
      setUploadError(result.error || "Upload failed");
      return;
    }
    uploadAiTimeline().catch(() => {});
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch (_) {}
    toast.success("Interview recording saved");
    setUploading(false);
    nav(goCoding ? `/invite/${token}/coding` : `/invite/${token}/done`);
  };

  // Allow the candidate to skip ahead without the recording — their text
  // answers are already saved, so the interview is still evaluable. Only
  // offered after an upload failure so we never silently drop a recording.
  const skipUpload = async () => {
    let goCoding = true;
    try {
      const { data } = await api.get(`/invite/${token}`);
      goCoding = !!(data?.job?.stages?.coding);
    } catch (_) {}
    toast.error("Continuing without the video recording.");
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch (_) {}
    setUploading(false);
    nav(goCoding ? `/invite/${token}/coding` : `/invite/${token}/done`);
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="video-room">
      <header className="border-b border-[color:var(--bd-sub)] px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4 min-w-0"><BrandMark tenant={brand} /><div className="text-xs uppercase tracking-wider text-slate-500 shrink-0" data-testid="video-room-title">AI Video Interview · with Ava</div></div>
        <div className="flex items-center gap-3 text-xs">
          {proctorFlags.tab_switches > 0 && <span className="tp-chip tp-chip-amber"><AlertTriangle className="w-3 h-3 mr-1"/>tab switches: {proctorFlags.tab_switches}</span>}
          {uploading && <span className="tp-chip tp-chip-indigo"><Upload className="w-3 h-3 mr-1"/>Uploading…</span>}
        </div>
      </header>

      {!started && photoNeeded && <PhotoCheck token={token} source="video" onDone={photoDone} />}
      {!started ? (
        <div className="flex-1 grid place-items-center p-8">
          <div className="tp-card p-8 max-w-md text-center" data-testid="start-gate">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-cyan-400 mx-auto mb-4 grid place-items-center"><Volume2 className="w-7 h-7 text-white"/></div>
            <div className="font-display text-xl font-semibold mb-2">Ready for your interview?</div>
            <div className="text-sm text-slate-400 mb-6">Ava, your AI interviewer, will speak each question with a natural voice and listen to your answer. Ava finishes each question before she starts listening. Your camera and microphone stay on for the full conversation. Please use <b>Chrome or Edge</b>.</div>
            {!hasSR && <div data-testid="no-speech-support" className="mb-3 text-xs text-amber-300">This browser can't turn your voice into text. For the best experience use Chrome or Edge on a computer — otherwise you can type your answers.</div>}
            {resumeInfo && <div data-testid="resume-notice" className="mb-3 text-sm text-emerald-300">Welcome back — your earlier answers are saved. You'll continue from question {resumeInfo.question_index + 1}.</div>}
            {startError && <div data-testid="start-error" className="mb-3 text-sm text-rose-400">{startError}</div>}
            <button data-testid="begin-interview" onClick={beginInterview} disabled={photoNeeded !== false || !!startError} className="disabled:opacity-60 w-full py-3 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium">Start interview</button>
          </div>
        </div>
      ) : (
        <div className="flex-1 grid content-start grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_400px] gap-5 p-4 md:p-6 max-w-[1680px] mx-auto w-full min-h-0 relative">
          {uploading && (
            <div className="absolute inset-0 z-20 grid place-items-center bg-[color:var(--bg-void)]/80 backdrop-blur-sm" data-testid="finalizing-overlay">
              <div className="tp-card p-8 max-w-md text-center">
                {uploadError ? (
                  <>
                    <div className="w-12 h-12 rounded-full bg-rose-500/15 border border-rose-400/40 grid place-items-center mx-auto mb-4">
                      <MicOff className="w-6 h-6 text-rose-300"/>
                    </div>
                    <div className="font-display text-lg font-semibold mb-2">Recording upload failed</div>
                    <div className="text-sm text-slate-400 mb-4" data-testid="upload-error-text">{uploadError}</div>
                    <div className="text-xs text-slate-500 mb-4">Your answers are already saved. You can retry the upload (same recording, no need to redo the interview) or continue without the video.</div>
                    <div className="flex gap-2 justify-center">
                      <button
                        data-testid="retry-upload"
                        onClick={retryUpload}
                        className="text-sm px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium"
                      >Retry upload</button>
                      <button
                        data-testid="skip-upload"
                        onClick={skipUpload}
                        className="text-sm px-4 py-2 rounded-lg border border-[color:var(--bd-hi)] text-slate-200 hover:bg-white/5"
                      >Continue without</button>
                    </div>
                  </>
                ) : (
                  <>
                    <Loader2 className="w-10 h-10 text-indigo-400 mx-auto mb-4 animate-spin"/>
                    <div className="font-display text-lg font-semibold mb-2">Saving your interview recording…</div>
                    {uploadProgress.total > 0 ? (
                      <>
                        <div className="text-sm text-slate-200 mb-2" data-testid="upload-progress-text">
                          {(uploadProgress.loaded / (1024*1024)).toFixed(1)} MB of {(uploadProgress.total / (1024*1024)).toFixed(1)} MB uploaded ({uploadProgress.pct}%)
                        </div>
                        <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mb-4">
                          <div
                            data-testid="upload-progress-bar"
                            className="h-full bg-indigo-500 transition-all duration-200"
                            style={{ width: `${uploadProgress.pct}%` }}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="text-sm text-slate-400 mb-4">Preparing the recording for upload…</div>
                    )}
                    <div className="text-xs text-slate-500">Please keep this tab open. You'll move on automatically when the upload completes.</div>
                  </>
                )}
              </div>
            </div>
          )}
          <div className="tp-card p-4 lg:p-6 flex flex-col min-h-0 w-full">
            <VideoFrame videoRef={videoRef} aiSpeaking={aiSpeaking} voiceOn={voiceOn} listening={listening} idx={idx} total={questions.length} started={started}
              thinking={submitting || preparing} followUp={isFollowUp}
              question={introPlaying ? "Ava is introducing herself…" : currentQuestion || questions[idx] || "Loading…"}>
              <AvaTile speaking={voiceOn} />
            </VideoFrame>
            <div className="mt-2 flex justify-center" data-testid="video-toolbar">
            <ControlBar aiSpeaking={aiSpeaking} listening={listening} submitting={submitting || preparing} typeMode={typeMode} hasSR={hasSR}
              currentQuestion={currentQuestion} canSubmit={!submitting && !aiSpeaking && !!transcript.trim()}
              onResumeMic={() => (typeMode ? switchTypeMode(false) : resumeMicRef.current())} onSubmit={submitAnswer} />
            </div>
            <MicMeter level={micLevel} listening={listening} aiSpeaking={aiSpeaking} className="mt-4 pt-3 border-t border-white/5 w-full max-w-xl mx-auto" />
          </div>
          <TranscriptPanel transcript={transcript} typeMode={typeMode} hasSR={hasSR}
            disabled={submitting || aiSpeaking} terms={skillTerms(jobSkills)}
            onType={(v) => { transcriptRef.current = v; setTranscript(v); }} onToggleType={() => switchTypeMode(!typeMode)}
            onEdit={editTranscript} onClear={clearTranscript} />
        </div>
      )}
      <PoweredBy />
    </div>
  );
}
